/**
 * Brewing stand screen (vanilla BrewingStandScreen 176x166): bottles (56,51) (79,58) (102,51),
 * ingredient (79,17), blaze powder fuel (17,17). The fuel bar, brew arrow and bubbles are drawn
 * from the stand state like the vanilla sprites.
 */

import { BREW_TICKS, FUEL_CHARGES, type BrewingStandState } from '../../game/brewing/stand';
import { brewingMenu } from '../../game/menu';
import { GuiStage, sheet } from '../kit';
import { ContainerScreen } from './container';
import { attachRecipeList } from './recipeBook';
import { brewingRows, brewingRowName } from '../../game/recipeBook';
import type { ScreenDeps } from './screenHost';

/** Vanilla bubble sprite heights, indexed by (brewTime / 2) % 7. */
const BUBBLE_LENGTHS = [29, 24, 20, 16, 11, 6, 0];
const SPR = 'url(mc/gui/sprites/container/brewing_stand/';
const PIX = 'image-rendering:pixelated;pointer-events:none;';

export class BrewingScreen extends ContainerScreen {
  private readonly fuelBar: HTMLDivElement;
  private readonly arrow: HTMLDivElement;
  private readonly bubbles: HTMLDivElement;

  constructor(deps: ScreenDeps, stage: GuiStage, private readonly state: BrewingStandState) {
    super(deps, stage);
    this.root.appendChild(sheet('gui/container/brewing_stand.png', 0, 0, 176, 166, 0, 0));
    // 0..2 bottles, 3 ingredient, 4 fuel, 5..31 main, 32..40 hotbar
    this.use(brewingMenu(this.env(), state));
    this.at(0, 56, 51); this.at(1, 79, 58); this.at(2, 102, 51); this.at(3, 79, 17); this.at(4, 17, 17);
    this.placePlayer(5, 84, 142);
    this.addLabel('Brewing Stand', 88, 6, 'center');
    this.addLabel('Inventory', 8, 72);
    attachRecipeList(this, { x: 4, y: 50 }, 'Brewing', brewingRows(),
      (r) => [brewingRowName(r), ...(r.note ? ['\u00a77' + r.note] : [])]);
    const mk = (css: string): HTMLDivElement => {
      const d = document.createElement('div');
      d.style.cssText = `position:absolute;${PIX}${css}`;
      this.root.appendChild(d);
      return d;
    };
    this.fuelBar = mk(`left:60px;top:44px;height:4px;background:${SPR}fuel_length.png) no-repeat;background-size:18px 4px;`);
    this.arrow = mk(`left:97px;top:16px;width:9px;background:${SPR}brew_progress.png) no-repeat;background-size:9px 28px;`);
    this.bubbles = mk(`left:63px;width:12px;background:${SPR}bubbles.png) no-repeat;background-size:12px 29px;`);
    this.mount();
    this.update();
  }

  override update(): void {
    const st = this.state;
    const fw = st.fuelLeft > 0 ? Math.min(18, Math.floor((18 * st.fuelLeft + FUEL_CHARGES - 1) / FUEL_CHARGES)) : 0;
    this.fuelBar.style.width = fw + 'px';
    if (st.brewTime > 0) {
      const ah = Math.min(28, Math.floor(28 * (1 - st.brewTime / BREW_TICKS)));
      this.arrow.style.display = ah > 0 ? '' : 'none';
      this.arrow.style.height = ah + 'px';
      const bl = BUBBLE_LENGTHS[Math.floor(st.brewTime / 2) % 7];
      this.bubbles.style.display = bl > 0 ? '' : 'none';
      this.bubbles.style.height = bl + 'px';
      this.bubbles.style.top = 14 + 29 - bl + 'px';
      this.bubbles.style.backgroundPosition = `0 ${bl - 29}px`;
    } else {
      this.arrow.style.display = 'none';
      this.bubbles.style.display = 'none';
    }
  }
}
